import type { CountryCode } from '../internal-billing'

const SUPPORTED_CURRENCIES = ['EUR', 'USD', 'MXN', 'PEN', 'COP', 'ARS', 'CLP'] as const

export type CurrencyFormat = (typeof SUPPORTED_CURRENCIES)[number]

const formats = {
  EUR: 'es-ES',
  USD: 'en-EN',
  GBP: 'en-EN',
  MXN: 'es-MX',
  PEN: 'es-PE',
  COP: 'es-CO',
  ARS: 'es-AR',
  CLP: 'es-CL'
} as const

const CURRENCY_EXPONENT: Record<CurrencyFormat, number> = {
  EUR: 2,
  USD: 2,
  MXN: 2,
  PEN: 2,
  COP: 2,
  ARS: 2,
  CLP: 0
}

export function currencyFormatters(
  currency: CurrencyFormat,
  milis = false,
  rounded = false
): Intl.NumberFormat {
  const maxFractionDigits = milis ? 3 : 2
  const config = {
    style: 'currency',
    currency: currency ?? 'EUR',
    maximumFractionDigits: maxFractionDigits,
    minimumFractionDigits: rounded ? 0 : maxFractionDigits
  } as const
  return new Intl.NumberFormat(formats[currency], config)
}

export function getMinorUnitExponent(currency: CurrencyFormat): number {
  return CURRENCY_EXPONENT[currency]
}

export function toMajorUnit(value: number, currency: CurrencyFormat): number {
  return value / Math.pow(10, getMinorUnitExponent(currency))
}

export function toMinorUnit(value: number, currency: CurrencyFormat): number {
  return Math.round(value * Math.pow(10, getMinorUnitExponent(currency)))
}

export function currencyFilter(
  value: number,
  currency: CurrencyFormat = 'EUR',
  milis = false,
  rounded = false
): string {
  const resolvedCurrency = SUPPORTED_CURRENCIES.includes(currency) ? currency : 'EUR'
  const divisor = milis ? 1000 : Math.pow(10, getMinorUnitExponent(resolvedCurrency))
  const decimalValue = value / divisor
  return currencyFormatters(resolvedCurrency, milis, rounded).format(decimalValue)
}

export function currencySymbol(currency: CurrencyFormat = 'EUR'): string {
  return currencyFilter(0, currency)
    .replace(/\d|[,.]/g, '')
    .trim()
}

export function getCurrencyIcon(currencyCode: string) {
  switch (currencyCode) {
    case 'MXN':
      return 'currency'
    case 'EUR':
      return 'euro'
    case 'USD':
      return 'currency'
    default:
      return 'euro'
  }
}

export function getCurrencyIconV2(currencyCode: string) {
  switch (currencyCode) {
    case 'EUR':
      return 'lucide/euro'
    default:
      return 'lucide/circle-dollar-sign'
  }
}

export function toMajorCurrency(value: number): number {
  return (value / 100).toFixed(2) as unknown as number
}

export const currencies: Record<CountryCode, CurrencyFormat> = {
  ES: 'EUR',
  AD: 'EUR',
  DE: 'EUR',
  MX: 'MXN',
  PE: 'PEN',
  CO: 'COP',
  AR: 'ARS',
  CL: 'CLP'
}

export function getCurrencyByCountry(countryCode: CountryCode): CurrencyFormat {
  return currencies[countryCode] || 'EUR'
}
